import React, { useState, useEffect } from "react";
import {
  Activity,
  CheckCircle,
  XCircle,
  DollarSign,
  Server,
  ShieldCheck,
  TrendingUp,
  LayoutDashboard,
  Zap,
  Search,
  FileSearch,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Fingerprint,
} from "lucide-react";
import { Hash, Globe, BarChart3, Layers } from "lucide-react";
import { getTopUsedService } from "../../services/dashboardService";

// --- 1. Beautiful Skeleton Loader (Optimized for 5-Column Grid) ---
const SkeletonCard = () => {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-md border border-slate-100 w-full min-h-[320px] flex flex-col justify-between">
      {/* Shimmer animation overlay */}
      <div className="absolute inset-0 -translate-x-full animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite] bg-gradient-to-r from-transparent via-blue-50/40 to-transparent" />

      <div>
        <div className="flex items-start justify-between mb-5">
          <div className="h-12 w-12 rounded-xl bg-slate-400 animate-pulse" />
          <div className="h-6 w-14 rounded-full bg-slate-400 animate-pulse" />
        </div>

        <div className="space-y-2 mb-5">
          <div className="h-5 w-5/6 rounded-md bg-slate-400 animate-pulse" />
          <div className="h-3 w-1/2 rounded-md bg-slate-400 animate-pulse" />
        </div>

        <div className="space-y-3">
          <div className="h-14 w-full rounded-xl bg-slate-400 animate-pulse" />
          <div className="h-14 w-full rounded-xl bg-slate-400 animate-pulse" />
        </div>
      </div>

      <div className="space-y-2 mt-4">
        <div className="h-3 w-full rounded-full bg-slate-400 animate-pulse" />
        <div className="flex justify-between">
          <div className="h-3 w-12 rounded-md bg-slate-400 animate-pulse" />
          <div className="h-3 w-12 rounded-md bg-slate-400 animate-pulse" />
        </div>
      </div>
    </div>
  );
};

const ServiceCard = ({ item }) => {
  const successRate = parseFloat(item?.success_percentage || 0).toFixed(1);
  const totalAmount = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(item?.total_amount || 0);

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white p-[1.5px] shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-900/20">
      {/* Premium Dark Blue to Dark Orange Gradient (Acts as border normally, fills card on hover) */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-blue-900 to-orange-600 opacity-40 transition-opacity duration-500 group-hover:opacity-100" />

      {/* Inner Card Content - Shifts to transparent/glassmorphism on hover */}
      <div className="relative h-full flex flex-col justify-between rounded-[15px] bg-white p-5 transition-colors duration-500 group-hover:bg-transparent">
        <div>
          {/* Top Row: Icon and Vendor Badge */}
          <div className="mb-4 flex items-start justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 text-orange-600 shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:from-white/20 group-hover:to-white/10 group-hover:text-white group-hover:backdrop-blur-md group-hover:shadow-lg">
              <ShieldCheck size={24} strokeWidth={2} />
            </div>
            <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 shadow-sm transition-all duration-500 group-hover:border-white/20 group-hover:bg-white/10 group-hover:text-white group-hover:backdrop-blur-md">
              {item?.vendor_name || "Vendor"}
            </span>
          </div>

          {/* Service Meta */}
          <div className="mb-5">
            <h3 className="line-clamp-1 text-base font-bold leading-tight tracking-tight text-blue-950 transition-colors duration-500 group-hover:text-white">
              {item?.service_name || "Service Name"}
            </h3>
            <div className="mt-1 flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-500 group-hover:text-blue-100">
              <Server
                size={12}
                className="shrink-0 transition-colors duration-500 group-hover:text-blue-200"
              />
              <span className="truncate" title={item?.end_point}>
                {item?.end_point || "/api/v1/endpoint"}
              </span>
            </div>
          </div>

          {/* Stats Blocks (Glassmorphism on Hover) */}
          <div className="mb-5 space-y-2.5">
            {/* Total Requests */}
            <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-2.5 transition-all duration-500 group-hover:border-white/10 group-hover:bg-white/10 group-hover:backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <Activity
                  size={15}
                  className="text-blue-900 transition-colors duration-500 group-hover:text-blue-300"
                />
                <span className="text-xs font-bold tracking-wider text-slate-500 uppercase transition-colors duration-500 group-hover:text-blue-100">
                  Requests
                </span>
              </div>
              <p className="text-sm font-black text-blue-950 transition-colors duration-500 group-hover:text-white">
                {Number(item?.total_requests || 0).toLocaleString()}
              </p>
            </div>

            {/* Revenue */}
            <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-2.5 transition-all duration-500 group-hover:border-white/10 group-hover:bg-white/10 group-hover:backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <DollarSign
                  size={15}
                  className="text-orange-600 transition-colors duration-500 group-hover:text-orange-300"
                />
                <span className="text-xs font-bold tracking-wider text-slate-500 uppercase transition-colors duration-500 group-hover:text-orange-100">
                  Revenue
                </span>
              </div>
              <p className="text-sm font-black text-blue-950 transition-colors duration-500 group-hover:text-white">
                {totalAmount}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section: Progress Bar & Mini Metrics */}
        <div className="space-y-2.5 border-t border-slate-100 pt-3 transition-colors duration-500 group-hover:border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <TrendingUp
                size={15}
                className="text-orange-600 transition-colors duration-500 group-hover:text-orange-300"
              />
              <span className="text-xs font-bold text-blue-950 transition-colors duration-500 group-hover:text-white">
                Success Rate
              </span>
            </div>
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-black text-blue-900 transition-colors duration-500 group-hover:bg-white/20 group-hover:text-white">
              {successRate}%
            </span>
          </div>

          {/* Progress Bar */}
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 shadow-inner transition-colors duration-500 group-hover:bg-black/20">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-950 via-blue-900 to-orange-600 transition-all duration-1000 ease-out group-hover:from-blue-400 group-hover:via-orange-400 group-hover:to-orange-300"
              style={{ width: `${successRate}%` }}
            />
          </div>

          <div className="flex justify-between text-[11px] font-bold">
            <span className="flex items-center gap-1 text-slate-600 transition-colors duration-500 group-hover:text-blue-50">
              <CheckCircle
                size={12}
                className="shrink-0 text-blue-900 transition-colors duration-500 group-hover:text-blue-300"
              />
              {Number(item?.success_requests || 0).toLocaleString()}
            </span>
            <span className="flex items-center gap-1 text-slate-500 transition-colors duration-500 group-hover:text-orange-50">
              <XCircle
                size={12}
                className="shrink-0 text-orange-600 transition-colors duration-500 group-hover:text-orange-300"
              />
              {Number(item?.failed_requests || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 3. Main Dashboard Component ---
export default function TopServicesDashboard() {
  const [isInitializing, setIsInitializing] = useState(true);

  const [topUsedServices, setTopUsedServices] = useState(null);
  const [loading, setLoading] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationData, setPaginationData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const getTopServices = async () => {
    const payload = {
      from_date: fromDate,
      to_date: toDate,
      page,
    };
    setLoading(true);
    try {
      const res = await getTopUsedService(payload);
      if (res?.status?.code === 200) {
        setHasFetched(true);
        setTopUsedServices(res?.data);
        setPaginationData(res?.data?.pagination);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   getTopServices();
  // }, []);

  const handleFetch = () => {
    getTopServices();
  };

  useEffect(() => {
    if (!hasFetched) return;
    getTopServices();
  }, [page]);

  const isLoading = isInitializing || !topUsedServices;

  return (
    <div className=" bg-slate-50/60 p-4 pb-24 h-screen overflow-y-auto modern-scrollbar font-sans antialiased">
      <div className="max-w-[1800px] mx-auto">
        {/* Modern Main Heading Section */}
        <div className="relative mb-4 p-2 rounded-3xl bg-gradient-to-r from-blue-950 to-blue-900 text-white overflow-hidden shadow-xl shadow-blue-950/10">
          {/* Decorative design background elements */}
          <div className="absolute right-0 top-0 -mt-4 -mr-4 w-72 h-72 rounded-full bg-orange-600/10 blur-3xl pointer-events-none" />
          <div className="absolute left-1/3 bottom-0 w-48 h-48 rounded-full bg-blue-500/10 blur-2xl pointer-events-none" />

          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-orange-500 shadow-inner">
                <LayoutDashboard size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white">
                    <Zap size={10} fill="currentColor" /> Live Metrics
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">
                  Core Analytics{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-500">
                    & Dashboard Hub
                  </span>
                </h1>
              </div>
            </div>

            <div className="text-left sm:text-right border-t sm:border-t-0 border-white/10 pt-3 sm:pt-0">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-300">
                System Integration
              </p>
              <p className="text-xl font-black text-orange-400 mt-0.5">
                Top Performing Services
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-4">
          {/* Glassmorphic Container */}
          <div className="relative w-full max-w-3xl p-8 overflow-hidden backdrop-blur-2xl bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-blue-700  border border-white/10 rounded-[2rem] shadow-[0_8px_32px_0_rgba(0,0,0,0.3)]">
            {/* Ambient background glows for the liquid/mesh effect */}
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-600/20 rounded-full mix-blend-screen filter blur-[100px] pointer-events-none"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-orange-600/10 rounded-full mix-blend-screen filter blur-[100px] pointer-events-none"></div>

            <div className="relative z-10">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 tracking-tight">
                  Get Top Services
                </h2>
                <p className="text-slate-400 mt-2 text-sm">
                  Select a date range to retrieve records.
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-5 items-end">
                {/* From Date Input */}
                <div className="flex-1 w-full group">
                  <label
                    htmlFor="fromDate"
                    className="block text-sm font-medium text-slate-300 mb-2 ml-1 group-focus-within:text-orange-400 transition-colors"
                  >
                    From Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="fromDate"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      // [color-scheme:dark] ensures the native calendar icon matches the dark theme
                      className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all duration-300 backdrop-blur-md [color-scheme:dark] hover:bg-white/10"
                    />
                  </div>
                </div>

                {/* To Date Input */}
                <div className="flex-1 w-full group">
                  <label
                    htmlFor="toDate"
                    className="block text-sm font-medium text-slate-300 mb-2 ml-1 group-focus-within:text-orange-400 transition-colors"
                  >
                    To Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="toDate"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all duration-300 backdrop-blur-md [color-scheme:dark] hover:bg-white/10"
                    />
                  </div>
                </div>

                {/* Fetch Button */}
                <button
                  title={loading ? "Loading..." : "Fetch"}
                  onClick={handleFetch}
                  disabled={loading || !fromDate || !toDate}
                  className="w-full disabled:opacity-50 disabled:cursor-not-allowed md:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold rounded-2xl shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] transform hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-slate-900 active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="animate-spin" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FileSearch />
                      Fetch
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {topUsedServices?.data?.length > 0 && (
          <div className="my-2 flex justify-end">
            <div className="inline-flex items-center gap-2 rounded-2xl bg-white p-2 shadow-md ring-1 ring-gray-200">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-indigo-100 hover:text-indigo-600 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-5 py-2 text-sm font-semibold text-white shadow">
                {page} / {paginationData?.total}
              </div>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === paginationData?.total}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-indigo-100 hover:text-indigo-600 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Ultra Wide 5-Column Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4   gap-5">
          {loading ? (
            // Render 5 sleek skeletons to map the initial layout look perfectly
            Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonCard key={`skeleton-${idx}`} />
            ))
          ) : topUsedServices?.data?.length > 0 ? (
            // Render Actual Live Data
            topUsedServices.data?.map((item, idx) => (
              <ServiceCard key={item?.service_id || idx} item={item} />
            ))
          ) : (
            // Empty State Handling
            <div className="col-span-full flex flex-col items-center justify-center p-16 bg-white rounded-3xl border border-dashed border-slate-300 shadow-inner">
              <Activity className="h-14 w-14 text-slate-300 mb-3 animate-pulse" />
              <h3 className="text-lg font-bold text-blue-950">
                No API Data Streams Found
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Active metrics will populate on system request calls.
              </p>
            </div>
          )}
        </div>

        {topUsedServices?.overall_response_codes &&
          topUsedServices?.service_response_codes && (
            <ResponseMetrics topUsedServices={topUsedServices} />
          )}
      </div>
    </div>
  );
}

function ResponseMetrics({ topUsedServices }) {
  return (
    <div className="flex flex-col gap-5 p-6 bg-slate-950 rounded-md mt-8 min-h-screen text-slate-200 font-sans">
      {/* ----------------------------------------- */}
      {/* OVERALL RESPONSE CODES */}
      {/* ----------------------------------------- */}
       <h2 className="text-xl font-bold text-orange-500 flex items-center gap-2 border-b border-slate-800 pb-2">
            <BarChart3 className="w-5 h-5" />
            Overall Status Codes
          </h2>
     <div className="flex flex-wrap gap-3">
  {Object.entries(topUsedServices.overall_response_codes).map(
    ([code, total]) => (
      <div
        key={code}
        className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900 px-4 py-2 hover:border-orange-500 transition"
      >
        <span
          className={`rounded-md px-2 py-1 text-xs font-semibold
            ${
              code === "200"
                ? "bg-green-500/10 text-green-400"
                : code.startsWith("4")
                ? "bg-yellow-500/10 text-yellow-400"
                : code.startsWith("5")
                ? "bg-red-500/10 text-red-400"
                : "bg-slate-700 text-slate-300"
            }`}
        >
          {code}
        </span>

        <span className="text-lg font-bold text-white">
          {Number(total).toLocaleString()}
        </span>
      </div>
    )
  )}
</div>

      {/* ----------------------------------------- */}
      {/* SERVICE RESPONSE CODES */}
      {/* ----------------------------------------- */}
      {topUsedServices?.service_response_codes && (
        <div className="flex flex-col gap-5 mt-8">
          <h2 className="text-xl font-bold text-orange-500 flex items-center gap-2 border-b border-slate-800 pb-2">
            <Layers className="w-5 h-5" />
            Service Response Details
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {Object.entries(topUsedServices.service_response_codes).map(
              ([key, value]) => {
                const service = value?.[0];

                return (
                  <div
                    key={key}
                    className="bg-slate-900 border border-slate-800 rounded-xl p-3 shadow-lg hover:border-orange-500/50 transition-all"
                  >
                    {/* Header */}
                    <div className="border-b border-slate-800 pb-4 mb-4">
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
                        <Server className="w-5 h-5 text-orange-500" />
                        {service?.service_name}
                      </h3>

                      <div className="mt-3 space-y-2 text-sm">
                        {/* <div className="flex items-center gap-2 text-slate-400">
                          <Hash className="w-4 h-4 text-orange-500" />
                          <span>Service ID: {service?.service_id}</span>
                        </div> */}

                        <div className="flex items-start gap-2 text-slate-400">
                          <Globe className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                          <span className="font-mono break-all">
                            {service?.end_point}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Response Table */}
                    <div className="overflow-hidden rounded-lg border border-slate-800">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-950">
                          <tr>
                            <th className="px-3 py-2 text-left text-slate-400 font-medium">
                              Response
                            </th>
                            <th className="px-3 py-2 text-right text-slate-400 font-medium">
                              Requests
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {value.map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-t border-slate-800 hover:bg-slate-800/40"
                            >
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold
                        ${
                          item.response_code === "200"
                            ? "bg-green-500/10 text-green-400"
                            : item.response_code === "404"
                              ? "bg-yellow-500/10 text-yellow-400"
                              : item.response_code === "500"
                                ? "bg-red-500/10 text-red-400"
                                : "bg-slate-700 text-slate-300"
                        }`}
                                >
                                  {item.response_code ?? "NULL"}
                                </span>
                              </td>

                              <td className="px-3 py-2 text-right font-semibold text-slate-100">
                                {item.total.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}
    </div>
  );
}
